<!-- eslint-disable max-len -->
<template>
  <div
    class="my-16 p-8 bg-white rounded text-gray-800"
    v-if="fund.details.name !== undefined"
  >
    <!-- Page 1 -->
    <div style="break-after: page;">
      <p
        class="font-bold text-center text-lg"
        v-if="fund.trustee.type === 'corporate'"
      > RESOLUTION OF {{ fund.trustee.company.name | uppercase }}</p>
      <p
        class="font-bold text-center text-lg"
        v-else
      > RESOLUTION OF {{ formatTrustees | uppercase }}</p>
      <p
        class="font-bold text-center"
      >AS TRUSTEE OF {{ fund.details.name | uppercase }}</p>
      <table class="mt-16 table-auto w-full">
        <tbody>
          <tr>
            <td class="px-4 py-2 font-bold">Investment Strategy</td>
            <td class="px-4 py-2">
              The Trustee has reviewed the Fund’s investment strategy dated
              {{ lastReviewDated | moment('D MMMM YYYY') }}.
            </td>
          </tr>
          <tr v-if="invStratReview.review.type !== 'Yes'">
            <td class="px-4 py-2"></td>
            <td class="px-4 py-2">
              <p>At this time it was resolved to make no changes.</p>
              <p class="mt-2">
                It was noted that in making this decision the Trustee considered
                a range of matters including:
              </p>
              <ul class="list-none mt-2">
                <li class="mt-2 flex">
                  <span>(a)</span>
                  <span class="ml-2 block"
                    >The risk involved in making, holding and realising, and the
                    likely return from, the Fund's investments having regard to
                    its objectives and its expressed cash flow
                    requirements;</span
                  >
                </li>
                <li class="mt-2 flex">
                  <span>(b)</span>
                  <span class="ml-2 block"
                    >The composition of the Fund's investments as a whole
                    including the extent to which the investments are diverse or
                    involve the Fund being exposed to risks from inadequate
                    diversification;</span
                  >
                </li>
                <li class="mt-2 flex">
                  <span>(c)</span>
                  <span class="ml-2 block"
                    >The liquidity of the Fund's investments having regard to
                    its expected cash flow requirements;</span
                  >
                </li>
                <li class="mt-2 flex">
                  <span>(d)</span>
                  <span class="ml-2 block"
                    >The ability of the Fund to discharge its existing and
                    prospective liabilities; and</span
                  >
                </li>
                <li class="mt-2 flex">
                  <span>(e)</span>
                  <span class="ml-2 block"
                    >Whether or not the Fund should provide insurance to members
                    and if so the form that insurance should take.</span
                  >
                </li>
              </ul>
            </td>
          </tr>
          <tr v-if="activeSelections.length > 0">
            <td class="px-4 py-2"></td>
            <td class="px-4 py-2">
              <p>
                The Trustee has resolved to review the Fund's investment
                strategy at this time because:
              </p>
              <ul>
                <li v-for="(section, key) in activeSelections" :key="key">
                  <div class="mt-2">
                    <div v-if="section.options.length > 0" class="mt-2">
                      <ul class="list-outside list-disc">
                        <li
                          v-for="(reason, idx) in section.options"
                          :key="idx"
                          class="ml-4"
                          :class="[
                            {
                              'ml-10':
                                idx > 0 &&
                                section.label !== 'Structural Changes',
                            },
                          ]"
                        >
                          {{ reason }}
                        </li>
                      </ul>
                    </div>
                    <div v-else class="mt-2">
                      <p class="text-gray-600 italic text-sm">No Selections</p>
                    </div>
                  </div>
                </li>
              </ul>
            </td>
          </tr>
          <tr v-if="sectionReasons.length > 0 && invStratReview.review.type === 'Yes'">
            <td class="px-4 py-2"></td>
            <td class="px-4 py-2">
              Nonetheless:
              <ul>
                <li v-for="(section, key) in sectionReasons" :key="key">
                  <div class="mt-2" v-if="section.options.length > 0">
                    <div class="mt-2">
                      <ul class="list-outside list-disc">
                        <li
                          v-for="reason in section.options"
                          :key="reason"
                          class="ml-4"
                        >
                          {{ reason }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td class="px-4 py-2"></td>
            <td class="px-4 py-2">
              <p>
                As a result, the Trustee considers the current investment
                strategy remains appropriate over the longer term and has
                resolved to make no changes at this time.
              </p>
            </td>
          </tr>
          <tr
            v-if="
              invStratReview.review.specific.otherReason.hasOtherReason ===
              'Yes'
            "
          >
            <td class="px-4 py-2"></td>
            <td class="px-4 py-2">
              <p>
                The Trustee notes that the impact of the following external
                factors on the Fund’s asset values and likely future investment
                returns has been considered but did not result in a change in
                the current investment strategy:
              </p>
              <ul class="list-outside list-disc">
                <li
                  v-for="reason in invStratReview.review.specific.otherReason
                    .otherReasons"
                  :key="reason"
                  class="ml-4 mt-1"
                >
                  {{
                    reason === 'other'
                      ? invStratReview.review.specific.otherReason.otherReason
                      : reason
                  }}
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td class="px-4 py-2"></td>
            <td class="px-4 py-2">
              <p>
                The Trustee will continue to monitor the Fund's investment strategy to ensure it remains appropriate. The next review will
                occur {{ nextReview }}.
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div style="break-after: page;">
      <p class="mt-16">Signed as a true and correct record</p>
      <p class="mt-16">Dated:______________________</p>
      <div class="mt-24 flex flex-wrap">
        <div
          v-for="(trustee, idx) in fund.trustee.trustees"
          :key="idx"
          class="w-1/2"
          :class="{ 'mt-16': idx > 1 }"
        >
          <p class="w-64 border-t-2 border-gray-800">
            {{ trustee.firstName }} {{ trustee.lastName }}
          </p>
          <p v-if="fund.trustee.type === 'corporate'">Director</p>
          <p v-else>Trustee</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'inv-strat-review-word-template',
  computed: {
    fund() {
      return this.$store.getters['fund/getFund'];
    },
    invStratReview() {
      return _.cloneDeep(this.$store.getters['toolkit/getItemData']);
    },
    formatMembers() {
      return this.fund.members
        .flatMap(member => `${member.firstName} ${member.lastName}`)
        .join(', ');
    },
    formatTrustees() {
      return this.fund.trustee.trustees
        .flatMap(trustee => `${trustee.firstName} ${trustee.lastName}`)
        .join(', ');
    },
    lastReviewDated() {
      return this.invStratReview.lastReview.date;
    },
    nextReview() {
      return this.invStratReview.nextReview.type;
    },
    reviewReasons() {
      return this.invStratReview.review.specific.reasons;
    },
    sectionReasons() {
      return Object.keys(
        this.invStratReview.review.specific.sectionReasons,
      ).map((key) => {
        const reasons = this.invStratReview.review.specific.sectionReasons[key]
          .selections;
        const otherIdx = reasons.findIndex(reason => reason === 'other');
        if (otherIdx > -1) {
          reasons.splice(
            otherIdx,
            1,
            this.invStratReview.review.specific.sectionReasons[key].other,
          );
        }
        return {
          label: key,
          options: reasons,
        };
      });
    },
    activeSelections() {
      return (
        Object.keys(this.reviewReasons)
          // Filter the selected review reasons to only those sections that have valid selections
          .filter((section) => {
            const selections = Object.keys(this.reviewReasons[section]).filter(
              (option) => {
                if (
                  this.reviewReasons[section][option].selection instanceof Array
                ) {
                  return (
                    this.reviewReasons[section][option].selection.length > 0
                  );
                }
                return this.reviewReasons[section][option].selection;
              },
            );
            return selections.length > 0;
          })
          // Rework the object structure to use in this component
          .flatMap(section => ({
            label: section,
            options: Object.keys(this.reviewReasons[section])
              .filter((option) => {
                if (
                  this.reviewReasons[section][option].selection instanceof Array
                ) {
                  return (
                    this.reviewReasons[section][option].selection.length > 0
                  );
                }
                return this.reviewReasons[section][option].selection;
              })
              .flatMap((option) => {
                if (
                  this.reviewReasons[section][option].selection instanceof Array
                ) {
                  const reasons = [
                    ...this.reviewReasons[section][option].selection,
                  ];
                  const otherIdx = this.reviewReasons[section][
                    option
                  ].selection.findIndex(reason => reason === 'other');
                  if (otherIdx > -1) {
                    reasons.splice(
                      otherIdx,
                      1,
                      this.reviewReasons[section][option].other,
                    );
                  }
                  return [option, ...reasons];
                }
                return option;
              }),
          }))
      );
    },
  },
};
</script>

<style></style>
